var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useMemo } from 'react';
import styled, { css, useTheme, } from 'styled-components';
import { margin, compose } from 'styled-system';
import { Link as RouterLink, } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import { addAllyTM } from '../tools/utils';
import { useButtonWidth } from '../Button/hooks';
import { stylesBySize, getIconMargin, buttonVariants, mapHexToColorName, } from '../Button/utils';
import { Icon } from '../Icon';
import { useDerivedSize } from '../Button/hooks/useDerivedSize';
import { muiBankTheme } from '../tools/theme';
import { isExternalURI } from '../tools/utils/isExternalURI';
var composedStyledSystemProps = compose(margin, buttonVariants);
var styledAnchorConfig = {
    /**
     * This is necessary because styled components will not pass
     * non-standard attributes to DOM elements if `styled` is wrapping
     * a native DOM element. However, if a custom component is wrapped
     * by `styled`, then all attributes/props will be passed. See
     * https://styled-components.com/docs/basics#passed-props
     */
    shouldForwardProp: function (prop, defaultValidatorFn) {
        return prop === 'allytmln' || defaultValidatorFn(prop);
    },
};
var linkStyles = css(function (props) {
    var variant = props.variant, $isUnderlined = props.$isUnderlined, $width = props.$width;
    return css(templateObject_2 || (templateObject_2 = __makeTemplateObject(["\n    ", "\n    ", "\n    ", ";\n    box-sizing: border-box;\n    width: ", ";\n  "], ["\n    ", "\n    ", "\n    ", ";\n    box-sizing: border-box;\n    width: ", ";\n  "])), composedStyledSystemProps, $isUnderlined ? 'text-decoration: underline;' : '', variant !== 'link' && props.size ? stylesBySize[props.size] : css(templateObject_1 || (templateObject_1 = __makeTemplateObject([""], [""]))), $width);
});
var StyledAnchor = styled.a.withConfig(styledAnchorConfig)(templateObject_3 || (templateObject_3 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), linkStyles);
var StyledRouterLink = styled(RouterLink)(templateObject_4 || (templateObject_4 = __makeTemplateObject(["\n  ", "\n"], ["\n  ", "\n"])), linkStyles);
export var getRouterTo = function (to, state) {
    if (typeof to !== 'string') {
        return to;
    }
    if (state) {
        return { pathname: to, state: state };
    }
    return { pathname: to };
};
var getIconProps = function (_a) {
    var icon = _a.icon, iconFill = _a.iconFill, theme = _a.theme, size = _a.size, content = _a.content;
    return (__assign({ icon: icon.map, size: size, fill: iconFill, ariaHidden: true }, (content ? getIconMargin(theme, icon === null || icon === void 0 ? void 0 : icon.position) : {})));
};
var IconWrapper = function (_a) {
    var icon = _a.icon, iconFill = _a.iconFill, theme = _a.theme, size = _a.size, children = _a.children;
    var iconProps = getIconProps({
        icon: icon,
        iconFill: iconFill,
        theme: theme,
        size: size,
        content: children,
    });
    if (icon.position === 'right') {
        return (React.createElement(React.Fragment, null,
            children,
            React.createElement(Icon, __assign({}, iconProps))));
    }
    return (React.createElement(React.Fragment, null,
        React.createElement(Icon, __assign({}, iconProps)),
        children));
};
var IconWithText = function (_a) {
    var icon = _a.icon, iconFill = _a.iconFill, theme = _a.theme, size = _a.size, children = _a.children;
    if (icon === null || icon === void 0 ? void 0 : icon.map) {
        return (React.createElement(IconWrapper, { icon: icon, iconFill: iconFill, theme: theme, size: size }, children));
    }
    return React.createElement(React.Fragment, null, children);
};
var Link = React.forwardRef(function (_a, ref) {
    var _b = _a.to, to = _b === void 0 ? '' : _b, text = _a.text, _c = _a.className, className = _c === void 0 ? '' : _c, onClick = _a.onClick, target = _a.target, _d = _a.isUnderlined, isUnderlined = _d === void 0 ? false : _d, id = _a.id, icon = _a.icon, _e = _a.withAllyTM, withAllyTM = _e === void 0 ? true : _e, disableFullWidthSmDown = _a.disableFullWidthSmDown, fullWidthMd = _a.fullWidthMd, state = _a.state, unsupportedContent = _a.unsupportedContent, props = __rest(_a, ["to", "text", "className", "onClick", "target", "isUnderlined", "id", "icon", "withAllyTM", "disableFullWidthSmDown", "fullWidthMd", "state", "unsupportedContent"]);
    var isExternal = (typeof to === 'string' && isExternalURI(to)) || !!props.download;
    var safeVariant = props.variant || 'link';
    var safeSize = props.variant !== 'link' ? props.size : undefined;
    var derivedSize = useDerivedSize(safeVariant, safeSize);
    var width = useButtonWidth(safeVariant, !!disableFullWidthSmDown, !!fullWidthMd).width;
    var linkUUID = React.useRef(uuid()).current;
    var safeId = id || "link-".concat(linkUUID);
    var allyTM = addAllyTM({
        content: text,
        id: safeId,
        elementType: 'interactive',
        withAllyTM: withAllyTM,
    });
    var baseProps = __assign({ className: className, onClick: onClick, ref: ref, $isUnderlined: isUnderlined, id: safeId, $width: width, rel: 'noreferrer noopener', target: target }, allyTM);
    // Theme provides the color hex value, but Icon needs the color name
    // So we find the color name that maps to the hex value
    var iconFill = useMemo(function () { var _a; return mapHexToColorName((_a = muiBankTheme.buttons[safeVariant]) === null || _a === void 0 ? void 0 : _a.color); }, [safeVariant]);
    var theme = useTheme();
    return isExternal ? (React.createElement(StyledAnchor, __assign({}, baseProps, { href: to }, (props.variant !== 'link' ? { size: derivedSize } : {}), props),
        React.createElement(IconWithText, __assign({ icon: icon, iconFill: iconFill, theme: theme }, (props.variant !== 'link' ? { size: derivedSize } : {})), text || unsupportedContent))) : (React.createElement(StyledRouterLink, __assign({}, baseProps, { to: getRouterTo(to, state) }, (props.variant !== 'link' ? { size: derivedSize } : {}), props),
        React.createElement(IconWithText, __assign({ icon: icon, iconFill: iconFill, theme: theme }, (props.variant !== 'link' ? { size: derivedSize } : {})), text || unsupportedContent)));
});
Link.defaultProps = {
    variant: 'link',
};
export { Link };
var templateObject_1, templateObject_2, templateObject_3, templateObject_4;
//# sourceMappingURL=Link.js.map